import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { cx } from "../../utils/cx";
import { IconButton } from "../IconButton";
import styles from "./Modal.module.scss";

const CloseGlyph = () => (
    <svg aria-hidden fill="none" height={20} viewBox="0 0 20 20" width={20}>
        <path
            d="M6 6l8 8M14 6l-8 8"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.8"
        />
    </svg>
);

export type ModalSize = "sm" | "md" | "lg";

const PORTALLED_MENU_SELECTOR =
    '[data-ui="autocomplete-menu"], [data-ui="product-search-autocomplete-menu"], [data-ui="header-actions-menu"], [data-radix-select-content], [data-radix-popper-content-wrapper]';

/** Radix outside events put the real DOM node on `detail.originalEvent`. */
const isOutsidePortalledMenu = (event: {
    target: EventTarget | null;
    detail?: { originalEvent?: Event };
}) => {
    const target =
        (event.detail?.originalEvent?.target as HTMLElement | null) ??
        (event.target as HTMLElement | null);
    return Boolean(target?.closest?.(PORTALLED_MENU_SELECTOR));
};

export interface ModalProps {
    /** Controlled visibility. */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Dialog title (visible). */
    title: ReactNode;
    /**
     * Optional visible description (body copy under the title). When omitted,
     * `assistiveDescription` is exposed only to screen readers.
     */
    description?: ReactNode;
    /**
     * Screen-reader copy when there is no visible `description`.
     */
    assistiveDescription?: ReactNode;
    /** Main content. */
    children: ReactNode;
    /** Footer actions (e.g. `Button` row). Omit for no footer. */
    footer?: ReactNode;
    /** Max width preset. Default `md`. */
    size?: ModalSize;
    /** Renders the header close control. Default `true`. */
    showCloseButton?: boolean;
    /**
     * When `false`, the body does not scroll — children fill height and
     * manage their own overflow (e.g. tab panels). Default `true`.
     */
    bodyScroll?: boolean;
    className?: string;
}

/**
 * Treez DS — **Modal** (centered dialog).
 *
 * Figma documentation page `8467:41171` (`qgFv3OQga6RQ4luvSFuGFP`).
 * Uses `@radix-ui/react-dialog` (focus trap, `Escape`, scroll lock).
 */
export function Modal({
    open,
    onOpenChange,
    title,
    description,
    assistiveDescription = "Dialog content.",
    children,
    footer,
    size = "md",
    showCloseButton = true,
    bodyScroll = true,
    className,
}: ModalProps) {
    const hasVisibleDescription =
        description != null &&
        description !== "" &&
        !(typeof description === "string" && description.trim() === "");

    return (
        <Dialog.Root modal onOpenChange={onOpenChange} open={open}>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.overlay} />
                <Dialog.Content
                    className={cx(styles.panel, className)}
                    data-size={size}
                    data-ui="modal"
                    onFocusOutside={event => {
                        // Radix CustomEvents expose the real node on
                        // `detail.originalEvent.target`, not `event.target`.
                        if (isOutsidePortalledMenu(event)) {
                            event.preventDefault();
                        }
                    }}
                    onInteractOutside={event => {
                        // Keep the modal open when interacting with portalled
                        // popovers rendered outside the dialog (e.g. the
                        // Autocomplete / Select menus).
                        if (isOutsidePortalledMenu(event)) {
                            event.preventDefault();
                        }
                    }}
                    onPointerDownOutside={event => {
                        if (isOutsidePortalledMenu(event)) {
                            event.preventDefault();
                        }
                    }}
                >
                    <header className={styles.header}>
                        <Dialog.Title asChild>
                            <h2 className={styles.title}>{title}</h2>
                        </Dialog.Title>
                        {showCloseButton ? (
                            <div className={styles.headerActions}>
                                <Dialog.Close asChild>
                                    <IconButton
                                        aria-label="Close dialog"
                                        size="sm"
                                        type="button"
                                        variant="tertiary"
                                    >
                                        <CloseGlyph />
                                    </IconButton>
                                </Dialog.Close>
                            </div>
                        ) : null}
                    </header>
                    <div
                        className={cx(
                            styles.body,
                            !bodyScroll && styles.bodyFill,
                        )}
                    >
                        {hasVisibleDescription ? (
                            <Dialog.Description asChild>
                                <div className={styles.description}>
                                    {description}
                                </div>
                            </Dialog.Description>
                        ) : (
                            <Dialog.Description className={styles.srOnly}>
                                {assistiveDescription}
                            </Dialog.Description>
                        )}
                        <div className={styles.bodyContent}>{children}</div>
                    </div>
                    {footer != null ? (
                        <footer className={styles.footer}>{footer}</footer>
                    ) : null}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

Modal.displayName = "Modal";
